import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { TextField, MenuItem } from "@mui/material";
import { Flag } from "@mui/icons-material";
import moment from "moment";
import UserReportAdminService, {
  UserReport,
} from "../services/UserReportAdminService.ts";

const REASON_LABELS: Record<string, string> = {
  spam: "Spam",
  harassment: "Harassment",
  inappropriate: "Inappropriate content",
  scam: "Scam or fraud",
  impersonation: "Impersonation",
  other: "Other",
};

const STATUS_COLORS: Record<string, string> = {
  pending: "#e65100",
  resolved: "#2e7d32",
  dismissed: "#757575",
};

const UserReportsManager: React.FC<{ user?: any }> = () => {
  const [reports, setReports] = useState<UserReport[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("pending");
  const [actingId, setActingId] = useState<string | null>(null);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const res = await UserReportAdminService.listReports(status, 1, 50);
      if (res.status) {
        setReports(res.data);
        setTotal(res.pagination.total);
      }
    } catch {
      toast.error("Failed to load user reports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const handleResolve = async (id: string, action: "resolve" | "dismiss") => {
    setActingId(id);
    try {
      const res = await UserReportAdminService.resolveReport(id, action);
      if (res.status) {
        toast.success("Report updated");
        fetchReports();
      } else {
        toast.error(res.error || "Failed to update report");
      }
    } catch {
      toast.error("Failed to update report");
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="container-fluid" style={{ maxWidth: 1100, paddingTop: 8 }}>
      <h3 className="fw-bold mb-4">User Reports</h3>

      <div className="row g-2 mb-4">
        <div className="col-md-3">
          <TextField
            fullWidth
            size="small"
            select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="resolved">Resolved</MenuItem>
            <MenuItem value="dismissed">Dismissed</MenuItem>
          </TextField>
        </div>
      </div>

      <div className="mb-3">
        <span className="text-muted small fw-600">{total} report(s)</span>
      </div>

      {loading ? (
        <div className="text-center py-5">Loading…</div>
      ) : reports.length > 0 ? (
        <div className="table-responsive">
          <table className="table table-hover user-management-table">
            <thead>
              <tr>
                <th>Reported User</th>
                <th>Reporter</th>
                <th>Reason</th>
                <th>Details</th>
                <th>Status</th>
                <th>Reported</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r._id}>
                  <td className="fw-600">{r.reportedUser?.user_name || "Unknown"}</td>
                  <td className="small">{r.reporter?.user_name || "Unknown"}</td>
                  <td>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        fontSize: "0.75rem",
                        fontWeight: 700,
                      }}
                    >
                      <Flag style={{ fontSize: 14, color: "#e65100" }} />
                      {REASON_LABELS[r.reason] || r.reason}
                    </span>
                  </td>
                  <td
                    className="small text-muted"
                    style={{
                      maxWidth: 260,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {r.details || "—"}
                  </td>
                  <td>
                    <span
                      style={{
                        background: STATUS_COLORS[r.status] || "#555",
                        color: "#fff",
                        padding: "2px 9px",
                        borderRadius: 5,
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        textTransform: "capitalize",
                      }}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="small text-muted">
                    {moment(r.createdAt).format("MMM D, YYYY")}
                  </td>
                  <td className="text-center">
                    {r.status === "pending" ? (
                      <div className="d-flex gap-1 justify-content-center">
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          style={{ fontSize: "0.72rem", padding: "2px 8px" }}
                          disabled={actingId === r._id}
                          onClick={() => handleResolve(r._id, "dismiss")}
                        >
                          Dismiss
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          style={{ fontSize: "0.72rem", padding: "2px 8px" }}
                          disabled={actingId === r._id}
                          onClick={() => handleResolve(r._id, "resolve")}
                        >
                          Mark Resolved
                        </button>
                      </div>
                    ) : (
                      <span className="text-muted small">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-5 text-muted">
          <p className="fw-bold">No reports found 🎉</p>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: any) => ({ user: state.auth.user });
export default connect(mapStateToProps)(UserReportsManager);
